import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import AxiosConfig from './AxiosConfig';
import msalAuth from './msalAuth';

// #region USETIMEOUT ============================================================
export const useTimeout = (callback: () => void, delay: number | null) => {
    const savedCallback: any = useRef();

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    useEffect(() => {
        function tick() {
            savedCallback.current();
        }

        if (delay !== null) {
            const id = setTimeout(tick, delay);
            return () => clearTimeout(id);
        }
    }, [delay]);
};
// #endregion

// #region USEINTERVAL ===========================================================
// const useInterval = (callback: () => void, delay: number | null) => {
//     const savedCallback: any = useRef();

//     useEffect(() => {
//         savedCallback.current = callback;
//     }, [callback]);

//     useEffect(() => {
//         function tick() {
//             savedCallback&&savedCallback.current();
//         }
//         if (delay !== null) {
//             const id = setInterval(tick, delay);
//             return () => clearInterval(id);
//         }
//     }, [delay]);
// };
// #endregion

// #region USEAXIOS ==============================================================
// Calls an endpoint as soon as a component mounts and returns response, error, and loading status's
export const useAxios = (url: string, options: any = {}, useMsal: boolean = false) => {
    const isCurrent = React.useRef(true);
    const [response, setResponse] = React.useState(null as any);
    const [error, setError] = React.useState(null as any);
    const [isLoading, setIsLoading] = React.useState(false);
    React.useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            let authorization = useMsal ? msalAuth.ConfigValue() : AxiosConfig.configValue("min")
            let config = {
                ...options,
                url: url,
                authorization
            }
            try {
                const res = await axios(config);
                isCurrent && setResponse(res);
                setIsLoading(false);
            } catch (error) {
                isCurrent && setError(error);
                setIsLoading(false);
            }
        }
        fetchData();
        return () => { isCurrent.current = false; }
        //r-18-lint-dj
        // eslint-disable-next-line 
    }, []);
    return { response, error, isLoading };
}
//? USAGE:
// const { response, loading, isLoading } = useAxios("url", {...options}, false)
// #endregion 

//#region USEPREVIOUS ============================================================
// const usePrevious = (value: any) => {
//     const ref = React.useRef();
//     React.useEffect(() => {
//         ref.current = value;   
//     });
//     return ref.current;
// }
//? Usage: const lastValue = usePrevious(value);

// ? NOTE: useLocalStorage is a default React hook -> Details on how to use/persist state with it: https://medium.com/@gbenussi/persisting-state-on-react-apps-726c310f35ed
// ? if data is NOT on localstorage -> set state to initial state
// ? if data IS on localstorage -> setState to stored state
// #endregion

// #region USEONCLICKOUTSIDE =====================================================
export const useOnClickOutside = (ref: any, handler: any, toIgnore: Array<string> | null = null) => {
    useEffect(() => {
        const listener = (event: any) => {
            // //console.log("Event: ", event, "Ignored: ", toIgnore);
            let stringified: string = event.path.map((e: any) => `${e.id} ${e.className}`).join("");
            // //console.log("Stringified: ", stringified);
            let ignored: boolean = toIgnore ? toIgnore?.reduce((acc: boolean, cur: string) => (stringified.includes(cur) || acc), false) : false;
            // Do nothing if clicking ref's element or descendent elements
            if (
                !ref.current ||
                ref.current.contains(event.target) ||
                ignored
            ) {
                return;
            }
            handler(event);
        };
        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);
        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
        // Add ref and handler to effect dependencies
        // It's worth noting that because passed in handler is a new ...
        // ... function on every render that will cause this effect ...
        // ... callback/cleanup to run every render. It's not a big deal ...
        // ... but to optimize you can wrap handler in useCallback before ...
        // ... passing it into this hook.
    }, [ref, handler, toIgnore]);
}
//? Usage
// const ref = useRef();
// const [open, setOpen] = React.useState(true);
// useOnClickOutside(ref, () => setOpen(false))
// in render put ref on target element
// #endregion

// #region USEDEBOUNCE ===========================================================
export const useDebounce = (value: any, delay: number = 1000) => {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = React.useState(value);
    React.useEffect(
        () => {
            // Update debounced value after delay
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);
            // Cancel the timeout if value changes (also on delay change or unmount)
            // This is how we prevent debounced value from updating if value is changed ...
            // .. within the delay period. Timeout gets cleared and restarted.
            return () => {
                clearTimeout(handler);
            };
        }, [value, delay] // Only re-call effect if value or delay changes
    );
    return debouncedValue;
}
//#endregion

// #region USEEVENTLISTENER ======================================================

// export const useEventListener = (eventType: string = "click", listener: Function, target = window, options: any = null) => {
//     const savedListener:any = useRef();

//     useEffect(() => {
//         savedListener.current = listener;
//     }, [listener])

//     useEffect(() => {
//         if(!target?.addEventListener){ return; }
//         const eventListener: any = savedListener.current(event);
//         //@ts-ignore
//         target.addEventListener(eventType, eventListener, options)
//         return () => {
//             target.removeEventListener(eventType, eventListener, options);
//         }
//     }, [eventType, target, options])
// }
// #endregion

// #region USELOCALSTORAGE =======================================================
// const useLocalStorage = (key = "", initialValue = "") => {
//     const [state, setState] = useState(() => {
//         try{
//             const item = localStorage.getItem(key);
//             return item ? JSON.parse(item) : initialValue;
//         }catch(err){
//             return initialValue;
//         }
//     })

//     const setLocalStorageState = (newState: any) => {
//         try{
//             const newValue = typeof newState === "function"
//                 ?newState(state)
//                 :newState
//             setState(newValue);
//             localStorage.setItem(key, JSON.stringify(newValue));
//         }catch(err: any) {
//             console.error(err);
//         }
//     }

//     return [state, setLocalStorageState]
// }
//? USAGE
// const [userSettings, setUserSettings] = useLocalStorage("user-settings", {color: "red", height: "6", etc...})
// #endregion 

